/**
 * Email Popup Manager
 * 
 * Manages the display and submission of the "Get My Recipe" email popup.
 * The popup appears after a configurable delay and persists its dismissed/submitted
 * state across page reloads and navigation using localStorage.
 */


// ORIGINAL JS

(function () {
    'use strict';
  
  /**
   * Configuration constants
   * @type {Object}
   */
  const CONFIG = {
    /** Delay in seconds before showing the popup */
    delaySeconds: 6,
    /** Brevo API endpoint for email subscription */
    // brevoEndpoint: 'https://brevo-eco-protein-popup.vercel.app/api/get-my-recipe',
    
    brevoEndpoint: 'https://eco-brevo-popup.abhishekrana14015.workers.dev/',
    /** localStorage key for tracking popup state */
    storageKey: 'ecoProteinPopupShown',
    /** Request timeout in milliseconds */
    requestTimeout: 10000,
    /** Auto-close success popup after this many milliseconds */
    successAutoCloseDelay: 5000,
    /** Number of days before popup can be shown again (expiration period) */
    expirationDays: 36500
  };
  
    /**
     * Subscribes an email address to Brevo.
     * @param {string} email - The email address to subscribe
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    async function subscribeToBrevo(email) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, CONFIG.requestTimeout);
  
      try {
        const response = await fetch(CONFIG.brevoEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            attributes: {
              SIGNUP_SOURCE: 'website_popup',
              SIGNUP_DATE: new Date().toISOString(),
              OFFER: '8_smoothie_recipes'
            }
          }),
          signal: controller.signal
        });
  
        clearTimeout(timeoutId);
  
        if (!response.ok) {
          let errorData = null;
          try {
            errorData = await response.json();
          } catch (e) {
            errorData = null;
          }
          const statusText = response.statusText || 'Unknown error';
          const statusCode = response.status;
          throw new Error(
            (errorData && errorData.message) ||
            `Subscription failed (${statusCode}: ${statusText})`
          );
        }
  
        return { success: true };
      } catch (error) {
        clearTimeout(timeoutId);
  
        let errorMessage = 'Unable to subscribe. Please try again.';
        const errorMsg = error && error.message ? error.message : '';
        const errorName = error && error.name ? error.name : '';
        const errorString = String(error);
  
        if (
          errorName === 'AbortError' ||
          errorMsg.indexOf('aborted') !== -1 ||
          errorMsg.indexOf('timeout') !== -1
        ) {
          errorMessage = 'Request timed out. Please check your connection and try again.';
        } else if (
          errorName === 'TypeError' &&
          (errorMsg.indexOf('Failed to fetch') !== -1 ||
            errorMsg.indexOf('NetworkError') !== -1 ||
            errorString.indexOf('Failed to fetch') !== -1)
        ) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (
          errorMsg.indexOf('CORS') !== -1 ||
          errorMsg.indexOf('cross-origin') !== -1 ||
          errorMsg.indexOf('Access-Control') !== -1
        ) {
          errorMessage = 'Connection blocked. Please contact support if this persists.';
        } else if (errorMsg && errorMsg !== 'Failed to fetch') {
          errorMessage = errorMsg;
        }
  
        console.error('Brevo subscription error:', {
          error: error,
          message: errorMsg,
          name: errorName,
          toString: errorString,
          endpoint: CONFIG.brevoEndpoint,
          stack: error && error.stack
        });
  
        return {
          success: false,
          error: errorMessage
        };
      }
    }
  
    /**
     * Validates an email address format.
     * @param {string} email - The email address to validate
     * @returns {boolean}
     */
    function validateEmail(email) {
      if (!email || typeof email !== 'string') return false;
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    }
  
  /**
   * Checks if the popup has been shown (dismissed or submitted) and if it's still within the expiration period.
   * Uses localStorage for persistence across page reloads and navigation.
   * @returns {boolean} True if popup was shown and hasn't expired, false otherwise
   */
  function hasPopupBeenShown() {
    try {
      const storedValue = localStorage.getItem(CONFIG.storageKey);
      if (!storedValue) return false;

      // Handle legacy format (just 'true' string) for backward compatibility
      if (storedValue === 'true') {
        return true;
      }

      // Parse the stored object with timestamp
      try {
        const data = JSON.parse(storedValue);
        if (!data || !data.timestamp) return false;

        // Check if expiration period has passed
        const expirationTime = data.timestamp + CONFIG.expirationDays * 24 * 60 * 60 * 1000;
        const now = Date.now();

        if (now >= expirationTime) {
          // Expired - remove from storage and allow popup to show again
          localStorage.removeItem(CONFIG.storageKey);
          return false;
        }

        return true;
      } catch (parseError) {
        // Invalid JSON format - treat as not shown
        console.warn('Invalid popup state format, resetting:', parseError);
        localStorage.removeItem(CONFIG.storageKey);
        return false;
      }
    } catch (e) {
      // Fallback to sessionStorage if localStorage is unavailable
      console.warn('localStorage unavailable, falling back to sessionStorage');
      const storedValue = sessionStorage.getItem(CONFIG.storageKey);
      if (!storedValue) return false;

      // For sessionStorage, use simple boolean check (no expiration)
      return storedValue === 'true';
    }
  }
  
  /**
   * Marks the popup as shown in storage with a timestamp.
   * Uses localStorage for persistence across page reloads and navigation.
   * Stores both a boolean flag and timestamp for expiration checking.
   */
  function markPopupAsShown() {
    const data = {
      shown: true,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(CONFIG.storageKey, JSON.stringify(data));
    } catch (e) {
      // Fallback to sessionStorage if localStorage is unavailable
      console.warn('localStorage unavailable, falling back to sessionStorage');
      // For sessionStorage, use simple boolean (no expiration)
      sessionStorage.setItem(CONFIG.storageKey, 'true');
    }
  }
  
    /**
     * Toggles the visibility of a popup overlay.
     * @param {string} id - The ID of the overlay element
     * @param {boolean} shouldShow - Whether to show or hide the overlay
     */
    function toggleOverlay(id, shouldShow) {
      const overlay = document.getElementById(id);
      if (!overlay) return;
  
      if (shouldShow) {
        overlay.classList.add('active');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            overlay.classList.add('show');
          });
        });
        document.body.style.overflow = 'hidden';
      } else {
        overlay.classList.remove('show');
        setTimeout(() => {
          overlay.classList.remove('active');
          document.body.style.overflow = '';
        }, 300);
      }
    }
  
    /**
     * Shows the email popup if it hasn't been shown before.
     */
    function showEmailPopup() {
      if (hasPopupBeenShown()) {
        return;
      }
      toggleOverlay('emailPopup', true);
  
      setTimeout(() => {
        const emailInput = document.getElementById('emailInput');
        if (emailInput) emailInput.focus();
      }, 400);
    }
  
    /**
     * Closes the email popup and marks it as shown.
     */
    function closeEmailPopup() {
      toggleOverlay('emailPopup', false);
      markPopupAsShown();
    }

  
    /**
     * Shows the success popup.
     */
    function showSuccessPopup() {
      toggleOverlay('emailPopupSuccess', true);
    }
  
    /**
     * Closes the success popup.
     */
    function closeSuccessPopup() {
      toggleOverlay('emailPopupSuccess', false);
    }

 /**
      * Shows the intent popup.
     */ 
    function showIntentPopup() {
    toggleOverlay('eco-intent-popup', true);
}

function closeIntentPopup() {
    toggleOverlay('eco-intent-popup', false);
}
  
    /**
     * Handles form submission for the email popup.
     * @param {Event} e - The submit event
     */
    async function handleFormSubmit(e) {
      if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }
  
      const emailInput = document.getElementById('emailInput');
      const submitButton = document.getElementById('submitButton');
      const errorMessage = document.getElementById('errorMessage');
  
      if (!emailInput || !submitButton || !errorMessage) {
        console.error('Email popup form elements not found', {
          emailInput: !!emailInput,
          submitButton: !!submitButton,
          errorMessage: !!errorMessage
        });
        return;
      }
  
      const email = emailInput.value.trim();
  
      emailInput.classList.remove('error');
      errorMessage.classList.remove('show');
      errorMessage.textContent = '';
  
      if (!validateEmail(email)) {
        emailInput.classList.add('error');
        errorMessage.textContent = 'Please enter a valid email address';
        errorMessage.classList.add('show');
        emailInput.focus();
        return;
      }
  
      submitButton.classList.add('loading');
      submitButton.disabled = true;
  
      try {
        const result = await subscribeToBrevo(email);
  
        submitButton.classList.remove('loading');
        submitButton.disabled = false;
  
        if (result && result.success) {
          // Mark popup as shown to prevent it from reappearing
          markPopupAsShown();
          closeEmailPopup();
          showIntentPopup();
          showSuccessPopup();
  
          // Track conversion event
          if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
            window.gtag('event', 'generate_lead', {
              event_category: 'engagement',
              event_label: 'email_popup_submission'
            });
          }
  
          // Auto-close success popup after delay
          // setTimeout(() => {
          //   closeSuccessPopup();
          // }, CONFIG.successAutoCloseDelay);

        } else {
          emailInput.classList.add('error');
          errorMessage.textContent =
            (result && result.error) || 'Unable to subscribe. Please try again.';
          errorMessage.classList.add('show');
        }
      } catch (error) {
        console.error('Form submission error:', error);
        submitButton.classList.remove('loading');
        submitButton.disabled = false;
        emailInput.classList.add('error');
        errorMessage.textContent = 'An unexpected error occurred. Please try again.';
        errorMessage.classList.add('show');
      }
    }
  
    /**
     * Initializes the email popup functionality.
     * Sets up event listeners and schedules the popup display.
     */
    function initEmailPopup() {
      // Schedule popup display after delay
      setTimeout(showEmailPopup, CONFIG.delaySeconds * 1000);
  
      // Close button for email popup
      const closeBtn = document.getElementById('closePopup');
      if (closeBtn) {
        closeBtn.addEventListener('click', closeEmailPopup);
      }
  
      // Close button for success popup
      const closeSuccessBtn = document.getElementById('closePopupSuccess');
      if (closeSuccessBtn) {
        closeSuccessBtn.addEventListener('click', closeSuccessPopup);
      }
  
      // Close email popup when clicking outside
      const overlayForm = document.getElementById('emailPopup');
      if (overlayForm) {
        overlayForm.addEventListener('click', (evt) => {
          if (evt.target === overlayForm) {
            closeEmailPopup();
          }
        });
      }
  
      // Close success popup when clicking outside
      const overlaySuccess = document.getElementById('emailPopupSuccess');
      if (overlaySuccess) {
        overlaySuccess.addEventListener('click', (evt) => {
          if (evt.target === overlaySuccess) {
            closeSuccessPopup();
          }
        });
      }
  
      // Close popups on Escape key
      document.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape') {
          const popup = document.getElementById('emailPopup');
          const successPopup = document.getElementById('emailPopupSuccess');
          if (popup && popup.classList.contains('active')) {
            closeEmailPopup();
          } else if (successPopup && successPopup.classList.contains('active')) {
            closeSuccessPopup();
          }
        }
      });
  
      // Form submission handler
      const form = document.getElementById('emailForm');
      if (form) {
        form.addEventListener('submit', handleFormSubmit);
      } else {
        console.warn('Email popup form not found');
      }
  
      // Clear error state on input
      const emailInput = document.getElementById('emailInput');
      if (emailInput) {
        emailInput.addEventListener('input', () => {
          emailInput.classList.remove('error');
          const errorMsg = document.getElementById('errorMessage');
          if (errorMsg) errorMsg.classList.remove('show');
        });
      }
    }
  
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initEmailPopup);
    } else {
      initEmailPopup();
    }

  })();

function copyEcoCode(btn) {
  var code = 'ECO10';
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(code).then(function () {
      var old = btn.textContent;
      btn.textContent = '✓';
      setTimeout(function(){ btn.textContent = old; }, 1200);
    });
  }
}
  
  
  







/**
 * ******************
 * *****************
 * **** New Popup Js *****
 * *****************
 */

// (function () {
//     'use strict';
  
//   /**
//    * Configuration constants
//    * @type {Object}
//    */
//  const CONFIG = {

//     // Sales pages
//     salesDelay: 6,

//     // Blog Desktop
//     blogDesktopDelay: 10,
//     blogDesktopScroll: 40,

//     // Blog Mobile
//     blogMobileDelay: 13,
//     blogMobileScroll: 50,

//     brevoEndpoint: 'https://eco-brevo-popup.abhishekrana14015.workers.dev/',

//     requestTimeout:10000,

//     successAutoCloseDelay:5000,

//     shownForHours:24,

//     closedForDays:3

// };
  
//     /**
//      * Subscribes an email address to Brevo.
//      * @param {string} email - The email address to subscribe
//      * @returns {Promise<{success: boolean, error?: string}>}
//      */
//     async function subscribeToBrevo(email) {
//       const controller = new AbortController();
//       const timeoutId = setTimeout(() => {
//         controller.abort();
//       }, CONFIG.requestTimeout);
  
//       try {
//         const response = await fetch(CONFIG.brevoEndpoint, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json'
//           },
//           body: JSON.stringify({
//             email: email,
//             attributes: {
//               SIGNUP_SOURCE: 'website_popup',
//               SIGNUP_DATE: new Date().toISOString(),
//               OFFER: '8_smoothie_recipes'
//             }
//           }),
//           signal: controller.signal
//         });
  
//         clearTimeout(timeoutId);
  
//         if (!response.ok) {
//           let errorData = null;
//           try {
//             errorData = await response.json();
//           } catch (e) {
//             errorData = null;
//           }
//           const statusText = response.statusText || 'Unknown error';
//           const statusCode = response.status;
//           throw new Error(
//             (errorData && errorData.message) ||
//             `Subscription failed (${statusCode}: ${statusText})`
//           );
//         }
  
//         return { success: true };
//       } catch (error) {
//         clearTimeout(timeoutId);
  
//         let errorMessage = 'Unable to subscribe. Please try again.';
//         const errorMsg = error && error.message ? error.message : '';
//         const errorName = error && error.name ? error.name : '';
//         const errorString = String(error);
  
//         if (
//           errorName === 'AbortError' ||
//           errorMsg.indexOf('aborted') !== -1 ||
//           errorMsg.indexOf('timeout') !== -1
//         ) {
//           errorMessage = 'Request timed out. Please check your connection and try again.';
//         } else if (
//           errorName === 'TypeError' &&
//           (errorMsg.indexOf('Failed to fetch') !== -1 ||
//             errorMsg.indexOf('NetworkError') !== -1 ||
//             errorString.indexOf('Failed to fetch') !== -1)
//         ) {
//           errorMessage = 'Network error. Please check your internet connection and try again.';
//         } else if (
//           errorMsg.indexOf('CORS') !== -1 ||
//           errorMsg.indexOf('cross-origin') !== -1 ||
//           errorMsg.indexOf('Access-Control') !== -1
//         ) {
//           errorMessage = 'Connection blocked. Please contact support if this persists.';
//         } else if (errorMsg && errorMsg !== 'Failed to fetch') {
//           errorMessage = errorMsg;
//         }
  
//         console.error('Brevo subscription error:', {
//           error: error,
//           message: errorMsg,
//           name: errorName,
//           toString: errorString,
//           endpoint: CONFIG.brevoEndpoint,
//           stack: error && error.stack
//         });
  
//         return {
//           success: false,
//           error: errorMessage
//         };
//       }
//     }
  
//     /**
//      * Validates an email address format.
//      * @param {string} email - The email address to validate
//      * @returns {boolean}
//      */
//     function validateEmail(email) {
//       if (!email || typeof email !== 'string') return false;
//       const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//       return regex.test(email);
//     }
  
//   /**
//    * Checks if the popup has been shown (dismissed or submitted) and if it's still within the expiration period.
//    * Uses localStorage for persistence across page reloads and navigation.
//    * @returns {boolean} True if popup was shown and hasn't expired, false otherwise
//    */

//   /**
//    * Marks the popup as shown in storage with a timestamp.
//    * Uses localStorage for persistence across page reloads and navigation.
//    * Stores both a boolean flag and timestamp for expiration checking.
//    */

// const STORAGE = {

//     SHOWN: "eco_popup_shown",

//     CLOSED: "eco_popup_closed",

//     SUBMITTED: "eco_popup_submitted"

// };

// function isPopupBlocked(){

//     if(localStorage.getItem(STORAGE.SUBMITTED))
//         return true;

//     const shownUntil=Number(localStorage.getItem(STORAGE.SHOWN)||0);

//     const closedUntil=Number(localStorage.getItem(STORAGE.CLOSED)||0);

//     const now=Date.now();

//     if(now<shownUntil)
//         return true;

//     if(now<closedUntil)
//         return true;

//     return false;

// }

// function markPopupShown(){

//     const expire=Date.now()+(CONFIG.shownForHours*60*60*1000);

//     localStorage.setItem(STORAGE.SHOWN,expire);

// }

// function markPopupClosed(){

//     const expire=Date.now()+(CONFIG.closedForDays*24*60*60*1000);

//     localStorage.setItem(STORAGE.CLOSED,expire);

// }

// function markPopupSubmitted(){

//     localStorage.setItem(STORAGE.SUBMITTED,"1");

// }
  
//     /**
//      * Toggles the visibility of a popup overlay.
//      * @param {string} id - The ID of the overlay element
//      * @param {boolean} shouldShow - Whether to show or hide the overlay
//      */
//     function toggleOverlay(id, shouldShow) {
//       const overlay = document.getElementById(id);
//       if (!overlay) return;
  
//       if (shouldShow) {
//         overlay.classList.add('active');
//         requestAnimationFrame(() => {
//           requestAnimationFrame(() => {
//             overlay.classList.add('show');
//           });
//         });
//         document.body.style.overflow = 'hidden';
//       } else {
//         overlay.classList.remove('show');
//         setTimeout(() => {
//           overlay.classList.remove('active');
//           document.body.style.overflow = '';
//         }, 300);
//       }
//     }
  
//     /**
//      * Shows the email popup if it hasn't been shown before.
//      */

// function showEmailPopup() {

//     toggleOverlay('emailPopup', true);

//     const successPopup =
//     document.getElementById("emailPopupSuccess");
//     if(successPopup){
//         toggleOverlay("emailPopupSuccess",false);
//     }
//     setTimeout(() => {
//         const emailInput = document.getElementById("emailInput");
//         if(emailInput) emailInput.focus();
//     },400);

// }
  
//     /**
//      * Closes the email popup and marks it as shown.
//      */
// function closeEmailPopup(markClosed = true){
//     toggleOverlay('emailPopup', false);
//     if(markClosed){
//         markPopupClosed();
//     }
// }

//     /**
//      * Shows the success popup.
//      */
//     function showSuccessPopup() {
//       toggleOverlay('emailPopupSuccess', true);
//     }
  
//     /**
//      * Closes the success popup.
//      */
//     function closeSuccessPopup() {
//       toggleOverlay('emailPopupSuccess', false);
//     }

//  /**
//       * Shows the intent popup.
//      */ 
// function showIntentPopup(){
//     if(isPopupBlocked())
//         return;
//     toggleOverlay("eco-intent-popup",true);
// }

// function closeIntentPopup(markClosed = true) {
//     if(markClosed){
//         markPopupShown();   // 24 hours
//         markPopupClosed();  // 3 days
//     }
//     toggleOverlay('eco-intent-popup', false);
// }
  
//     /**
//      * Handles form submission for the email popup.
//      * @param {Event} e - The submit event
//      */
//     async function handleFormSubmit(e) {
//       if (e && typeof e.preventDefault === 'function') {
//         e.preventDefault();
//       }
  
//       const emailInput = document.getElementById('emailInput');
//       const submitButton = document.getElementById('submitButton');
//       const errorMessage = document.getElementById('errorMessage');
  
//       if (!emailInput || !submitButton || !errorMessage) {
//         console.error('Email popup form elements not found', {
//           emailInput: !!emailInput,
//           submitButton: !!submitButton,
//           errorMessage: !!errorMessage
//         });
//         return;
//       }
  
//       const email = emailInput.value.trim();
  
//       emailInput.classList.remove('error');
//       errorMessage.classList.remove('show');
//       errorMessage.textContent = '';
  
//       if (!validateEmail(email)) {
//         emailInput.classList.add('error');
//         errorMessage.textContent = 'Please enter a valid email address';
//         errorMessage.classList.add('show');
//         emailInput.focus();
//         return;
//       }
  
//       submitButton.classList.add('loading');
//       submitButton.disabled = true;
  
//       try {
//         const result = await subscribeToBrevo(email);
  
//         submitButton.classList.remove('loading');
//         submitButton.disabled = false;
  
// if (result && result.success) {
//     // Never show popup again
//     markPopupSubmitted();

//     // Close email popup WITHOUT marking it as manually closed
//     closeEmailPopup(false);

//     // Show success popup
//     showSuccessPopup();

// // Auto close success popup
// // setTimeout(function () {
// //     closeSuccessPopup();
// // }, CONFIG.successAutoCloseDelay);

//     // Track conversion event
//     if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
//         window.gtag('event', 'generate_lead', {
//             event_category: 'engagement',
//             event_label: 'email_popup_submission'
//         });
//     }

// } else {
//           emailInput.classList.add('error');
//           errorMessage.textContent =
//             (result && result.error) || 'Unable to subscribe. Please try again.';
//           errorMessage.classList.add('show');
//         }
//       } catch (error) {
//         console.error('Form submission error:', error);
//         submitButton.classList.remove('loading');
//         submitButton.disabled = false;
//         emailInput.classList.add('error');
//         errorMessage.textContent = 'An unexpected error occurred. Please try again.';
//         errorMessage.classList.add('show');
//       }
//     }
  
//     /**
//      * Initializes the email popup functionality.
//      * Sets up event listeners and schedules the popup display.
//      */
//     function initEmailPopup() {

//     //    if (window.location.pathname === '/pages/partners') {
//     //     return;
//     // }
//      const excludedPages = [
//       '/pages/partners',
//       '/pages/partner-refill'
//    ];

//    if (excludedPages.includes(window.location.pathname)) {
//       return;
//    }
    
//       // Schedule popup display after delay
//       const pageType = window.ecoPopup?.pageType || "";

// const isBlog =
// pageType==="blog" ||
// pageType==="article";

// const isMobile =
// window.innerWidth<=768;

// let popupTriggered=false;
// let popupTimer;

// function triggerPopup(){
//     if(popupTriggered)
//         return;
//     popupTriggered = true;
//     clearTimeout(popupTimer);
//     showIntentPopup();
// }

// if(isBlog){

//     const delay=isMobile
//     ?CONFIG.blogMobileDelay
//     :CONFIG.blogDesktopDelay;

//     const scroll=isMobile
//     ?CONFIG.blogMobileScroll
//     :CONFIG.blogDesktopScroll;

// popupTimer =
// setTimeout(triggerPopup, delay * 1000);

// function onScroll(){

//     if(popupTriggered) return;

//     const pageHeight =
//         document.documentElement.scrollHeight - window.innerHeight;

//     if(pageHeight <= 0) return;

//     const percent =
//         (window.scrollY / pageHeight) * 100;

//     if(percent >= scroll){

//         window.removeEventListener("scroll", onScroll);

//         triggerPopup();

//     }

// }

// window.addEventListener("scroll", onScroll);

// }
// else{

// popupTimer =
// setTimeout(triggerPopup, CONFIG.salesDelay * 1000);

// }
  
//       // Close button for email popup
//       const closeBtn = document.getElementById('closePopup');
//       if (closeBtn) {
//         closeBtn.addEventListener('click', closeEmailPopup);
//       }
  
//       // Close button for success popup
//       const closeSuccessBtn = document.getElementById('closePopupSuccess');
//       if (closeSuccessBtn) {
//         closeSuccessBtn.addEventListener('click', closeSuccessPopup);
//       }
  
//       // Close email popup when clicking outside
//       const overlayForm = document.getElementById('emailPopup');
//       if (overlayForm) {
//         overlayForm.addEventListener('click', (evt) => {
//           if (evt.target === overlayForm) {
//             closeEmailPopup();
//           }
//         });
//       }
  
//       // Close success popup when clicking outside
//       const overlaySuccess = document.getElementById('emailPopupSuccess');
//       if (overlaySuccess) {
//         overlaySuccess.addEventListener('click', (evt) => {
//           if (evt.target === overlaySuccess) {
//             closeSuccessPopup();
//           }
//         });
//       }
  
//       // Close popups on Escape key
// document.addEventListener('keydown', (evt) => {
//     if (evt.key !== 'Escape') return;
//     const intentPopup =
//     document.getElementById('eco-intent-popup');
//     const popup =
//     document.getElementById('emailPopup');
//     const successPopup =
//     document.getElementById('emailPopupSuccess');
//     if (
//         intentPopup &&
//         intentPopup.classList.contains('active')
//     ) {
//         closeIntentPopup();
//     }
//     else if (
//         popup &&
//         popup.classList.contains('active')
//     ) {
//         closeEmailPopup();
//     }
//     else if (
//         successPopup &&
//         successPopup.classList.contains('active')
//     ) {
//         closeSuccessPopup();
//     }
// });
  
//       // Form submission handler
//       const form = document.getElementById('emailForm');
//       if (form) {
//         form.addEventListener('submit', handleFormSubmit);
//       } else {
//         console.warn('Email popup form not found');
//       }

//       /* ==========================================
//    INTENT POPUP EVENTS
// ========================================== */

// // Close button
// const closeIntentBtn = document.getElementById('closeIntentPopup');

// if (closeIntentBtn) {
//     closeIntentBtn.addEventListener('click', function () {
//         closeIntentPopup();
//     });
// }


// // Outside click
// const intentOverlay = document.getElementById('eco-intent-popup');

// if (intentOverlay) {
//     intentOverlay.addEventListener('click', function (e) {

//         if (e.target === intentOverlay) {
//             closeIntentPopup();
//         }

//     });
// }


// // Intent buttons
// const intentButtons = document.querySelectorAll('#eco-intent-popup .eco-options button');

// intentButtons.forEach(function (button) {

//     button.addEventListener('click', function () {

//         // Save selected option
//         localStorage.setItem(
//             'ecoProteinIntent',
//             this.dataset.choice
//         );

// // Mark popup shown for 24 hours
// markPopupShown();

// // Close without 3-day suppression
// closeIntentPopup(false);

// // Open email popup
// showEmailPopup();

//         // Auto close success popup
//         // setTimeout(function () {

//         //     closeSuccessPopup();

//         // }, CONFIG.successAutoCloseDelay);

//     });

// });
  
//       // Clear error state on input
//       const emailInput = document.getElementById('emailInput');
//       if (emailInput) {
//         emailInput.addEventListener('input', () => {
//           emailInput.classList.remove('error');
//           const errorMsg = document.getElementById('errorMessage');
//           if (errorMsg) errorMsg.classList.remove('show');
//         });
//       }
//     }
  
//     // Initialize when DOM is ready
//     if (document.readyState === 'loading') {
//       document.addEventListener('DOMContentLoaded', initEmailPopup);
//     } else {
//       initEmailPopup();
//     }
//   })();
  



